// ============================================================================
// Broiler batch writes as audited server operations (migration 236, B5 + B5a)
// ============================================================================
// `ppp-v4` used to be written as a WHOLE array: every save upserted the farm's
// entire broiler history, so two editors overwrote each other row by row, the
// field autosave dropped keys its form did not carry, and a stale form could
// put back a week-4/6 average a weigh-in had stamped since. Migration 236 adds
// `broiler_apply_operation`, which changes ONE batch under the ppp-v4 row lock
// after checking expected-before values, and writes the Activity row and a
// request receipt in the same transaction.
//
// This module is the browser half:
//   * planBroilerBatchOperations turns "the array the caller wants" into the
//     few operations that say what the caller actually CHANGED -- the same
//     contract every existing call site already speaks, so none of them had to
//     learn a new one; and
//   * runBroilerBatchOperations sends them, each with one stable request id, so
//     a retry after a lost response replays the stored result instead of
//     applying twice.
//
// HOW A FIELD BECOMES PART OF A CHANGE, AND WHAT ITS EXPECTED VALUE IS
// --------------------------------------------------------------------
// Three copies of each row are in play:
//   fresh  the row as the server holds it right now (read at save time);
//   shown  that row after the in-memory boot repair (applyBroilerBootRepair),
//          which is what every screen actually renders;
//   base   the row as the caller last saw it -- App's mirror, or the row a
//          previous save confirmed.
// For each field of the row the caller wants (`next`):
//   next = fresh                       -> nothing to send.
//   next = base, base = shown          -> the caller kept a value the boot
//                                         repair derived (a planned batch that
//                                         became active on hatch day): send it,
//                                         so the repair persists as it always
//                                         did when that batch was saved.
//   next = base, base != shown         -> the caller's copy is stale and they
//                                         did not touch the field: send
//                                         NOTHING. This is the fix for the
//                                         stale week-4/6 average.
//   otherwise (the caller changed it)  -> send it. The expected-before is the
//                                         server value when the caller's base
//                                         agrees with what they were shown,
//                                         and the caller's stale base when it
//                                         does not -- so a change someone else
//                                         made in between comes back as a
//                                         CONFLICT instead of being overwritten.
// A field the caller's row does not carry is never sent, so no key is dropped.
// Rows the caller did not change at all produce no operation.
// ============================================================================

import type {SupabaseClient, SupabaseErrorLike} from './supabaseTypes.js';

export type BroilerRow = Record<string, unknown>;

export type BroilerOperationName =
  | 'batch.create'
  | 'batch.update'
  | 'batch.delete'
  | 'batch.documents'
  | 'batch.rename'
  | 'batch.week_avg';

export type BroilerOperation = {
  operation: BroilerOperationName;
  target: {batch_id: string} | {batch_ref: string};
  expected: Record<string, unknown>;
  change: Record<string, unknown>;
};

export type BroilerOperationPlan =
  | {ok: true; operations: BroilerOperation[]}
  | {ok: false; reason: 'unkeyed' | 'deleted-elsewhere'; batchId?: string};

/** `{"__absent": true}` is how the server spells "this key was not there". */
export const BROILER_ABSENT = Object.freeze({__absent: true});

// The keys an update may not carry: the identity, the name (rename cascades)
// and the document list (a patch by path). The server refuses them too.
const RESERVED = new Set(['id', 'name', 'documents']);

function isRow(value: unknown): value is BroilerRow {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function rowId(row: unknown): string | null {
  if (!isRow(row)) return null;
  const id = row.id;
  if (typeof id === 'string' && id.trim() !== '') return id.trim();
  if (typeof id === 'number' && Number.isFinite(id)) return String(id);
  return null;
}

/** The value as it will travel: JSON round-tripped, so `undefined` is "not sent". */
function wire(value: unknown): unknown {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

/** Structural equality of two JSON values, key order ignored. */
export function sameBroilerValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return canonical(wire(a)) === canonical(wire(b));
}

// The batch form writes every blank number as 0 (`parseFloat(v) || 0`), so a
// key a stored row never had comes back from the form as 0. For "did anything
// change", a missing key, null, '' and 0 are one value; otherwise the first
// save of every older batch would rewrite (and report) a dozen absent fields.
function isBlank(value: unknown): boolean {
  return value === undefined || value === null || value === '' || value === 0;
}

function sameForDiff(a: unknown, b: unknown): boolean {
  return sameBroilerValue(a, b) || (isBlank(a) && isBlank(b));
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
  if (isRow(value)) {
    return (
      '{' +
      Object.keys(value)
        .sort()
        .map((k) => JSON.stringify(k) + ':' + canonical(value[k]))
        .join(',') +
      '}'
    );
  }
  return JSON.stringify(value);
}

function byId(rows: readonly unknown[] | null | undefined): Map<string, BroilerRow> {
  const out = new Map<string, BroilerRow>();
  for (const row of rows || []) {
    const id = rowId(row);
    if (id && isRow(row)) out.set(id, row);
  }
  return out;
}

// An id-less row equal to an id-less stored (or base) row of the same name.
function unchangedUnkeyed(row: BroilerRow, fresh: readonly unknown[] | null, base: readonly unknown[] | null): boolean {
  if (typeof row.name !== 'string' || row.name === '') return false;
  const same = (rows: readonly unknown[] | null) =>
    (rows || []).some((r) => isRow(r) && rowId(r) === null && r.name === row.name && sameBroilerValue(r, row));
  return same(fresh) || same(base);
}

function beforeValue(row: BroilerRow | undefined, key: string): unknown {
  return row && Object.prototype.hasOwnProperty.call(row, key) && row[key] !== undefined
    ? wire(row[key])
    : BROILER_ABSENT;
}

function documentPaths(docs: unknown): string[] {
  if (!Array.isArray(docs)) return [];
  return docs.map((d) => (isRow(d) && typeof d.path === 'string' ? d.path : null)).filter((p): p is string => !!p);
}

export type PlanBroilerBatchOperationsArgs = {
  /** The server's rows right now (null = no ppp-v4 document yet). */
  fresh: readonly unknown[] | null;
  /** `fresh` after the in-memory boot repair: what the caller was shown. */
  shown: readonly unknown[] | null;
  /** What the caller last saw. May be empty (an unhydrated mirror). */
  base: readonly unknown[] | null;
  /** The array the caller wants, merged onto the server copy. */
  next: readonly unknown[];
  /** Rows the caller deliberately deleted. */
  removed?: readonly unknown[];
  /** A document list the caller set on one row. */
  documents?: {batchId: unknown; documents: unknown} | null;
};

/**
 * The operations that turn the server's rows into the caller's, saying only
 * what the caller changed. Pure; see the header for the field rule.
 */
export function planBroilerBatchOperations(args: PlanBroilerBatchOperationsArgs): BroilerOperationPlan {
  const fresh = byId(args.fresh);
  const shown = byId(args.shown);
  const base = byId(args.base);
  const removedIds = new Set<string>();
  for (const row of args.removed || []) {
    const id = rowId(row);
    if (id) removedIds.add(id);
  }
  const docsTarget = args.documents ? rowId({id: args.documents.batchId}) : null;
  const operations: BroilerOperation[] = [];

  for (const raw of args.next) {
    const id = rowId(raw);
    if (!id && isRow(raw) && unchangedUnkeyed(raw, args.fresh, args.base)) {
      // A legacy/seed row with no id that the caller did not change is carried
      // as it is. The server can only target a batch by id, so it is only an
      // EDIT of such a row that must refuse.
      continue;
    }
    if (!id || !isRow(raw)) return {ok: false, reason: 'unkeyed'};
    if (removedIds.has(id)) continue;
    const next = raw;
    const f = fresh.get(id);
    const b = base.get(id);

    if (!f) {
      // The caller had this batch and the server no longer does: someone
      // deleted it. Re-creating it here would resurrect a deleted batch.
      if (b) return {ok: false, reason: 'deleted-elsewhere', batchId: id};
      const change: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(next)) if (k !== 'id' && v !== undefined) change[k] = wire(v);
      operations.push({operation: 'batch.create', target: {batch_id: id}, expected: {}, change});
      continue;
    }
    const s = shown.get(id) || f;

    // ---- name (rename cascades every name-keyed reference) ----------------
    const nextName = typeof next.name === 'string' ? next.name.trim() : null;
    const freshName = typeof f.name === 'string' ? f.name : null;
    if (nextName && nextName !== freshName && !(b && typeof b.name === 'string' && b.name.trim() === nextName)) {
      const expectedName = !b || sameBroilerValue(b.name, s.name) ? freshName : b.name;
      operations.push({
        operation: 'batch.rename',
        target: {batch_id: id},
        expected: {name: expectedName ?? ''},
        change: {name: nextName},
      });
    }

    // ---- documents (a patch by storage path) ------------------------------
    if (docsTarget === id && args.documents) {
      const wanted = Array.isArray(args.documents.documents) ? args.documents.documents : [];
      const before = documentPaths(b ? b.documents : s.documents);
      const wantedPaths = documentPaths(wanted);
      const add = wanted.filter((d) => isRow(d) && typeof d.path === 'string' && !before.includes(d.path));
      const remove = before.filter((p) => !wantedPaths.includes(p));
      if (add.length || remove.length) {
        const change: Record<string, unknown> = {};
        if (add.length) change.add = wire(add);
        if (remove.length) change.remove = remove;
        operations.push({operation: 'batch.documents', target: {batch_id: id}, expected: {}, change});
      }
    }

    // ---- every other field ---------------------------------------------------
    const expected: Record<string, unknown> = {};
    const change: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(next)) {
      if (RESERVED.has(k) || k.startsWith('__') || v === undefined) continue;
      if (sameForDiff(v, f[k])) continue;
      if (b && sameForDiff(v, b[k])) {
        // Untouched by the caller. Persist it only when it is the repaired
        // value the caller was shown; a stale copy is never written back.
        if (!sameBroilerValue(b[k], s[k])) continue;
        expected[k] = beforeValue(f, k);
      } else {
        expected[k] = !b || sameBroilerValue(b[k], s[k]) ? beforeValue(f, k) : beforeValue(b, k);
      }
      change[k] = wire(v);
    }
    if (Object.keys(change).length) {
      // A row whose only differences are pending boot repairs, and that the
      // caller did not otherwise touch, is left for the save that does.
      const touched =
        !b ||
        operations.some((o) => 'batch_id' in o.target && o.target.batch_id === id) ||
        Object.keys(change).some((k) => !sameForDiff(next[k], b[k]));
      if (touched) operations.push({operation: 'batch.update', target: {batch_id: id}, expected, change});
    }
  }

  for (const id of removedIds) {
    if (fresh.has(id)) operations.push({operation: 'batch.delete', target: {batch_id: id}, expected: {}, change: {}});
  }
  return {ok: true, operations};
}

export type BroilerOperationResult = {
  ok: boolean;
  kind?: 'created' | 'updated' | 'deleted' | 'no_change' | 'conflict';
  conflicts?: {field: string; expected: unknown; current: unknown}[];
  rows?: {batch?: BroilerRow; batch_id?: string};
  replayed?: boolean;
  [key: string]: unknown;
};

export type BroilerOperationsOutcome =
  | {ok: true; results: BroilerOperationResult[]}
  | {ok: false; reason: 'conflict'; conflicts: {field: string; expected: unknown; current: unknown}[]; batchId: string}
  | {ok: false; reason: 'failed'; error: SupabaseErrorLike | null; operation: BroilerOperationName};

/** A request id that is stable for ONE user intent: generated once, reused on every retry of it. */
export function newBroilerRequestId(): string {
  const c = (globalThis as {crypto?: {randomUUID?: () => string}}).crypto;
  const rand =
    c && typeof c.randomUUID === 'function'
      ? c.randomUUID()
      : String(Date.now()) + '-' + Math.random().toString(36).slice(2);
  return 'bb-' + rand;
}

// A transport failure (no answer) is retried with the SAME request id, so an
// operation that committed but whose response was lost replays its receipt
// rather than applying twice. A database refusal is an answer and is not.
function isTransportFailure(error: SupabaseErrorLike | null | undefined): boolean {
  if (!error) return true;
  const code = (error as {code?: unknown}).code;
  return !(typeof code === 'string' && code.length > 0);
}

/** Send ONE operation; retries only a lost answer, with the same request id. */
export async function applyBroilerOperation(
  sb: SupabaseClient,
  op: BroilerOperation,
  requestId: string = newBroilerRequestId(),
  attempts = 3,
): Promise<{data: BroilerOperationResult | null; error: SupabaseErrorLike | null}> {
  let last: SupabaseErrorLike | null = null;
  for (let i = 0; i < attempts; i++) {
    try {
      const r = await sb.rpc('broiler_apply_operation', {
        p_request_id: requestId,
        p_operation: op.operation,
        p_target: op.target,
        p_expected: op.expected,
        p_change: op.change,
      });
      if (!r.error) return {data: (r.data as BroilerOperationResult) || null, error: null};
      last = r.error;
      if (!isTransportFailure(r.error)) break;
    } catch (error) {
      last = (error as SupabaseErrorLike) || null;
    }
  }
  return {data: null, error: last};
}

/**
 * Send a plan's operations in order. Stops at the first conflict or refusal,
 * so nothing after an unresolved conflict is applied; whatever already landed
 * is real and the next save re-plans from the server, so it is not repeated.
 */
export async function runBroilerBatchOperations(
  sb: SupabaseClient,
  operations: readonly BroilerOperation[],
): Promise<BroilerOperationsOutcome> {
  const results: BroilerOperationResult[] = [];
  for (const op of operations) {
    const {data, error} = await applyBroilerOperation(sb, op);
    if (error || !data) return {ok: false, reason: 'failed', error, operation: op.operation};
    if (data.kind === 'conflict') {
      const batchId = 'batch_id' in op.target ? op.target.batch_id : op.target.batch_ref;
      return {ok: false, reason: 'conflict', conflicts: data.conflicts || [], batchId};
    }
    results.push(data);
  }
  return {ok: true, results};
}

/** What the operator is told when a save did not go through. */
export function describeBroilerSaveFailure(outcome: Exclude<BroilerOperationsOutcome, {ok: true}>): string {
  if (outcome.reason === 'conflict') {
    const fields = outcome.conflicts.map((c) => c.field).join(', ');
    return (
      'Not Saved. Someone else changed ' +
      (fields || 'this batch') +
      ' since you opened it. Reload to see their change.'
    );
  }
  const message = outcome.error && (outcome.error as {message?: unknown}).message;
  return (
    'Not Saved. ' +
    (typeof message === 'string' && message ? message : 'The server did not answer.') +
    ' Retry the save.'
  );
}
